import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { LuChartColumn } from "react-icons/lu";
import NotFound from "../ul/NotFound";

const UserChart = ({ userData = [] }) => {
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  const countByDate = (data) =>
    data.reduce((acc, item) => {
      const dateValue = item.createdAt;
      if (dateValue) {
        const date = formatDate(dateValue);
        acc[date] = (acc[date] || 0) + 1;
      }
      return acc;
    }, {});

  const newUserCounts = useMemo(() => countByDate(userData), [userData]);
  const allDates = useMemo(
    () =>
      Object.keys(newUserCounts).sort(
        (a, b) =>
          new Date(a.split("/").reverse().join("-")) -
          new Date(b.split("/").reverse().join("-"))
      ),
    [newUserCounts]
  );
  const newUserValues = allDates.map((date) => newUserCounts[date] || 0);
  const hasData = userData.length > 0;

  const chartOption = {
    tooltip: {
      trigger: "axis",
      backgroundColor: "#1E0758",
      borderColor: "rgba(204, 187, 249, 0.35)",
      borderWidth: 1,
      textStyle: { color: "#EEE9FC", fontSize: 13, fontWeight: 500 },
      padding: [12, 16],
      borderRadius: 12,
      extraCssText: "box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);",
      axisPointer: {
        type: "shadow",
        shadowStyle: {
          color: "rgba(71, 17, 203,0.12)",
        },
      },
      formatter: function (params) {
        const param = params[0];
        return `<div style="font-weight:600;font-size:14px;color:#EEE9FC;">${param.name}</div>
          <div style="color:#EEE9FC;">
          <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:#6A35E8;margin-right:6px;"></span>
          New Users: <b>${param.value}</b>
          </div>`;
      },
    },
    legend: {
      data: ["New Users"],
      top: 20,
      left: 24,
      icon: "roundRect",
      itemWidth: 16,
      itemHeight: 10,
      itemGap: 24,
      textStyle: { color: "#5E5A7A", fontWeight: 600, fontSize: 13 },
    },
    grid: {
      left: "3%",
      right: "3%",
      bottom: "10%",
      top: "18%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: allDates,
      axisLine: { lineStyle: { color: "rgba(71, 17, 203,0.14)", width: 2 } },
      axisTick: { show: false },
      axisLabel: {
        color: "#5E5A7A",
        fontWeight: 500,
        fontSize: 12,
        margin: 12,
      },
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      splitLine: { lineStyle: { color: "rgba(71, 17, 203,0.08)", width: 1.5 } },
      axisLabel: { color: "#5E5A7A", fontSize: 12, fontWeight: 500 },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        name: "New Users",
        type: "bar",
        barWidth: 24,
        data: newUserValues,
        itemStyle: {
          color: "#4711CB",
          shadowColor: "rgba(71, 17, 203, 0.3)",
          shadowBlur: 10,
          borderRadius: [6, 6, 0, 0],
        },
        emphasis: {
          itemStyle: {
            borderColor: "#6A35E8",
            borderWidth: 3,
            shadowColor: "rgba(71, 17, 203, 0.5)",
            shadowBlur: 20,
          },
        },
        label: {
          show: true,
          position: "top",
          color: "#4711CB",
          fontWeight: 600,
          fontSize: 13,
        },
      },
    ],
  };

  // Summary calculations
  const totalUsers = newUserValues.reduce((a, b) => a + b, 0);

  return (
    <div className="ui-card-solid p-6 md:p-8 w-full mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="w-1 h-7 bg-gradient-to-b from-teal-400 to-cyan-500 rounded-full"></span>
          <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30">
            <LuChartColumn className="w-5 h-5" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-main-font">
            User Analytics Overview
          </h3>
        </div>
        {hasData && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-500/15 text-brand-teal border border-brand-teal/15">
            {allDates.length} {allDates.length === 1 ? "day" : "days"}
          </span>
        )}
      </div>
      {!hasData ? (
        <div className="flex items-center justify-center h-64">
          <NotFound />
        </div>
      ) : (
        <>
          <div className="relative w-full h-[400px] md:h-[450px]">
            <ReactECharts
              option={chartOption}
              style={{ height: "100%", width: "100%" }}
              notMerge
              lazyUpdate
              theme={null}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default UserChart;
